import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiCheck, FiHome, FiPlay } from "react-icons/fi";

function TaskSubmitted() {
  const { role } = useSelector((state) => state.auth.user);
  const [showConfetti, setShowConfetti] = useState(true);
  const [particles, setParticles] = useState([]);
  useEffect(() => {
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      color: ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#f59e0b'][Math.floor(Math.random() * 6)],
      delay: Math.random() * 3,
      duration: Math.random() * 3 + 2,
    }));
    setParticles(newParticles);
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);
  const SuccessIcon = () => (
    <div className="relative">
      {/* Outer ring with pulse animation */}
      <div className="absolute inset-0 w-32 h-32 bg-green-500/20 rounded-full animate-ping"></div>
      <div className="absolute inset-2 w-28 h-28 bg-green-500/30 rounded-full animate-pulse"></div>

      {/* Main success circle */}
      <div className="relative w-32 h-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center shadow-2xl">
        <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
          <FiCheck className="w-12 h-12 text-white animate-bounce" strokeWidth={3} />
        </div>
      </div>

      {/* Sparkle effects */}
      <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-pulse"></div>
      <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
      <div className="absolute top-1/2 -left-4 w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
    </div>
  );

  const FloatingParticle = ({ particle }) => (
    <div
      key={particle.id}
      className="absolute rounded-full opacity-70 animate-float"
      style={{
        left: `${particle.x}%`,
        top: `${particle.y}%`,
        width: `${particle.size}px`,
        height: `${particle.size}px`,
        backgroundColor: particle.color,
        animationDelay: `${particle.delay}s`,
        animationDuration: `${particle.duration}s`,
      }}
    />
  );

  const StatsCard = ({ icon: Icon, label, value, color }) => (
    <div className={`bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-white/80 text-sm font-medium">{label}</p>
          <p className="text-white text-lg font-bold">{value}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-cyan-500 to-blue-600 relative overflow-hidden flex items-center justify-center">
      {/* Animated background patterns */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl animate-pulse"></div>
      </div>

      {/* Floating confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {particles.map(particle => (
            <FloatingParticle key={particle.id} particle={particle} />
          ))}
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Success animation section */}
        <div className="mb-8 flex justify-center">
          <SuccessIcon />
        </div>

        {/* Main heading with gradient text */}
        <div className="mb-8">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 animate-slideInUp">
            Success!
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-white/90 mb-2 animate-slideInUp" style={{ animationDelay: '0.2s' }}>
            Task Submitted Successfully
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-slideInUp" style={{ animationDelay: '1s' }}>
          <NavLink
            to="/dashboard"
            className="group relative px-8 py-4 bg-white/20 backdrop-blur-md text-white rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl font-semibold min-w-[200px]"
          >
            <div className="flex items-center justify-center gap-3">
              <FiHome className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </NavLink>
          {role === "tasker" && (
            <NavLink
              to="/tasks"
              className="group relative px-8 py-4 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-2xl hover:from-green-600 hover:to-teal-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl font-semibold min-w-[200px]"
            >
              <div className="flex items-center justify-center gap-3">
                <FiPlay className="w-5 h-5" />
                <span>Continue Tasking</span>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </NavLink>
          )}
        </div>
      </div>
      <style jsx>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-10px) rotate(5deg);
          }
          50% {
            transform: translateY(-20px) rotate(-5deg);
          }
          75% {
            transform: translateY(-10px) rotate(3deg);
          }
        }
        
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default TaskSubmitted;